import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Modal, Progress, Row, Space, Table, Tag, Tooltip,
  Typography, message,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, DatabaseOutlined, EyeOutlined,
  HistoryOutlined, ReloadOutlined, StopOutlined, SyncOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader, StatCard, StatRow } from '../../components/shared';
import { L } from '../../styles/theme';
import {
  BatchItem, BatchRef, DatasetCard, DatasetsResponse, RunRef, SyncBatch, SyncRun, datasyncApi,
} from '../../api/datasync';

const { Text, Paragraph } = Typography;

const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const STATUS_META: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
  running: { color: 'processing', label: 'идёт', icon: <SyncOutlined spin /> },
  success: { color: 'success', label: 'успех', icon: <CheckCircleOutlined /> },
  partial: { color: 'warning', label: 'частично', icon: <StopOutlined /> },
  failed: { color: 'error', label: 'ошибка', icon: <CloseCircleOutlined /> },
  refused: { color: 'warning', label: 'отказано', icon: <StopOutlined /> },
};

const StatusTag: React.FC<{ status?: string }> = ({ status }) => {
  const m = STATUS_META[status || ''] ?? { color: 'default', label: status || '—', icon: null };
  return <Tag color={m.color} icon={m.icon as any}>{m.label}</Tag>;
};

/** Свежесть сухого прогона: применять можно только по свежим числам. */
function dryIsFresh(ref: RunRef | null, ttlHours: number): boolean {
  if (!ref || ref.status !== 'success' || !ref.finished_at) return false;
  return Date.now() - new Date(ref.finished_at).getTime() < ttlHours * 3600_000;
}

/** То же правило для пачки: «частично» тоже годится — числа посчитаны. */
function batchIsFresh(ref: BatchRef | null, ttlHours: number): boolean {
  if (!ref || !ref.finished_at) return false;
  if (ref.status !== 'success' && ref.status !== 'partial') return false;
  return Date.now() - new Date(ref.finished_at).getTime() < ttlHours * 3600_000;
}

const DataSyncPage: React.FC = () => {
  const [data, setData] = useState<DatasetsResponse | null>(null);
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [batches, setBatches] = useState<SyncBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<SyncRun | null>(null);
  const [batchDetail, setBatchDetail] = useState<SyncBatch | null>(null);
  const [confirm, setConfirm] = useState<DatasetCard | null>(null);
  const [confirmBatch, setConfirmBatch] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, r, b] = await Promise.all([
        datasyncApi.datasets(), datasyncApi.runs(undefined, 25), datasyncApi.batches(10),
      ]);
      setData(d);
      setRuns(r.items);
      setBatches(b.items);
      return d;
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить состояние наборов');
      return null;
    }
  }, []);

  // Прогон идёт минутами — страница опрашивает состояние, а не висит.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const d = await load();
      if (!alive) return;
      const busy = d?.running || d?.batch;
      timer.current = setTimeout(tick, busy ? 3000 : 20000);
    };
    setLoading(true);
    tick().finally(() => alive && setLoading(false));
    return () => {
      alive = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [load]);

  const start = async (ds: DatasetCard, mode: 'dry' | 'apply') => {
    try {
      await datasyncApi.start(ds.key, mode);
      message.success(mode === 'dry'
        ? `Считаю, что изменится в наборе «${ds.title}»`
        : `Применяю набор «${ds.title}»`);
      setConfirm(null);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось запустить прогон');
    }
  };

  const startBatch = async (mode: 'dry' | 'apply') => {
    try {
      const res = await datasyncApi.startBatch(mode);
      message.success(mode === 'dry'
        ? `Считаю, что изменится во всех наборах (${res.total})`
        : `Применяю все наборы (${res.total})`);
      setConfirmBatch(false);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось запустить обновление');
    }
  };

  const openDetail = async (id: number) => {
    try {
      setDetail(await datasyncApi.run(id));
    } catch {
      message.error('Не удалось открыть отчёт');
    }
  };

  const openBatch = async (id: number) => {
    try {
      setBatchDetail(await datasyncApi.batch(id));
    } catch {
      message.error('Не удалось открыть общий отчёт');
    }
  };

  const running = data?.running ?? null;
  const batch = data?.batch ?? null;
  const ttl = data?.dry_run_ttl_hours ?? 6;
  const batchFresh = batchIsFresh(data?.last_batch_dry ?? null, ttl);
  const busy = !!running || !!batch;

  /**
   * Заблокированная кнопка молчит, и это читается как «не работает» — поэтому
   * «Применить» остаётся живой, а несвежие числа объясняет модалка, из которой
   * подсчёт можно запустить тут же.
   */
  const applyOrExplain = (ds: DatasetCard, fresh: boolean) => {
    if (fresh) {
      ds.danger ? setConfirm(ds) : start(ds, 'apply');
      return;
    }
    Modal.confirm({
      title: `Числа по набору «${ds.title}» устарели`,
      okText: 'Посчитать, что изменится',
      cancelText: 'Отмена',
      content: (
        <Paragraph style={{ marginBottom: 0 }}>
          Применять можно только по свежим числам — не старше {ttl} ч.
          {ds.last_dry
            ? ` Последний подсчёт: ${fmt(ds.last_dry.finished_at || ds.last_dry.started_at)}.`
            : ' Подсчёта по этому набору ещё не было.'}
          {' '}Посчитать сейчас? Это ничего не пишет в базу, занимает пару минут.
        </Paragraph>
      ),
      onOk: () => start(ds, 'dry'),
    });
  };

  const applyAllOrExplain = () => {
    if (batchFresh) {
      setConfirmBatch(true);
      return;
    }
    Modal.confirm({
      title: 'Числа по всем наборам устарели',
      okText: 'Посчитать всё',
      cancelText: 'Отмена',
      content: (
        <Paragraph style={{ marginBottom: 0 }}>
          Применить всё можно только по свежим числам — не старше {ttl} ч.
          {data?.last_batch_dry
            ? ` Последний общий подсчёт: ${fmt(data.last_batch_dry.finished_at
                || data.last_batch_dry.started_at)}.`
            : ' Общего подсчёта ещё не было.'}
          {' '}Посчитать сейчас? В базу ничего не пишется.
        </Paragraph>
      ),
      onOk: () => startBatch('dry'),
    });
  };

  const runColumns: ColumnsType<SyncRun> = [
    { title: '#', dataIndex: 'id', width: 64 },
    { title: 'Набор', dataIndex: 'dataset_title', width: 210 },
    {
      title: 'Режим', dataIndex: 'mode', width: 130,
      render: (m: string) => m === 'dry'
        ? <Tag>сухой прогон</Tag>
        : <Tag>применение</Tag>,
    },
    { title: 'Статус', dataIndex: 'status', width: 120, render: (s) => <StatusTag status={s} /> },
    {
      title: 'Что изменилось', key: 'nums',
      render: (_, r) => {
        const s = r.stats;
        if (!s) return <Text type="secondary">—</Text>;
        const w = s.written || {};
        const real = r.mode === 'apply';
        return (
          <Space size={4} wrap>
            <Tag>+{real ? (w.added ?? 0) : s.added}</Tag>
            <Tag>~{real ? (w.changed ?? 0) : s.changed}</Tag>
            <Tag>закрыто {real ? (w.closed ?? 0) : s.missing}</Tag>
          </Space>
        );
      },
    },
    { title: 'Кто', dataIndex: 'started_by_name', width: 150, render: (v) => v || '—' },
    { title: 'Когда', dataIndex: 'started_at', width: 150, render: fmt },
    {
      title: '', key: 'act', width: 110,
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r.id)}>
          Отчёт
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Данные 2ГИС"
        subtitle="Обновление городских справочников из источника. Сначала — что изменится, потом — применить."
        extra={<Button icon={<ReloadOutlined />} onClick={load}>Обновить</Button>}
      />

      {/* ── Обновить всё: то, за чем сюда и приходят ─────────────────── */}
      <Card
        style={{ marginBottom: 16, borderColor: '#1677ff' }}
        title={<Space><DatabaseOutlined /><b>Обновить все наборы сразу</b></Space>}
        extra={data?.last_batch_apply && (
          <Button type="link" size="small" onClick={() => openBatch(data.last_batch_apply!.id)}>
            последний общий отчёт
          </Button>
        )}
      >
        {batch ? (
          <BatchProgress batch={batch} running={running} />
        ) : (
          <>
            <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
              Одно нажатие — наборы идут очередью друг за другом ({data?.batch_datasets.length ?? 11} шт.),
              в конце один общий отчёт. Отказ по одному набору не останавливает остальные.
              Замена районов кварталами 2ГИС в очередь не входит — она меняет устройство города
              и остаётся отдельной кнопкой ниже.
            </Paragraph>
            <Space wrap>
              <Button
                size="large"
                icon={<EyeOutlined />}
                disabled={busy}
                onClick={() => startBatch('dry')}
              >
                Посчитать всё
              </Button>
              <Button
                size="large"
                type="primary"
                icon={<ThunderboltOutlined />}
                disabled={busy}
                onClick={applyAllOrExplain}
              >
                Применить всё
              </Button>
              <Text type="secondary" style={{ fontSize: 12.5 }}>
                {batchFresh
                  ? `числа свежие — можно применять`
                  : `сначала «Посчитать всё» — применение идёт по числам не старше ${ttl} ч`}
                {data?.last_batch_dry &&
                  ` · последний общий подсчёт ${fmt(data.last_batch_dry.finished_at)}`}
              </Text>
            </Space>
          </>
        )}
      </Card>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Два шага, всегда"
        description={
          <>
            «Посмотреть, что изменится» — прогон вхолостую: он ничего не пишет, только считает.
            Кнопка «Применить» работает по свежим числам (не старше {ttl} ч). Перед записью
            снимается бэкап, пропавшие из источника объекты помечаются закрытыми, но
            <b> не удаляются</b>. Если из источника пропало больше 10% набора — прогон
            откажется применять.
          </>
        }
      />

      {running && !batch && (
        <Alert
          type="warning"
          showIcon
          icon={<SyncOutlined spin />}
          style={{ marginBottom: 16 }}
          message={`Идёт прогон #${running.id}: ${running.dataset_title} (${
            running.mode === 'dry' ? 'подсчёт' : 'применение'})`}
          description={running.progress
            ? <Text code style={{ fontSize: 12 }}>{running.progress}</Text>
            : 'Пока он не закончится, другие наборы запускать нельзя. Страница обновляется сама.'}
        />
      )}

      <StatRow span={6}>
        <StatCard label="Наборов" value={data?.items.length ?? '—'} icon={<DatabaseOutlined />} />
        <StatCard
          label="Строк всего"
          value={data ? data.items.reduce((a, i) => a + (i.rows || 0), 0).toLocaleString('ru-RU') : '—'}
        />
        <StatCard label="Прогонов в истории" value={runs.length} icon={<HistoryOutlined />} />
        <StatCard
          label="Последнее применение"
          value={fmt(runs.find((r) => r.mode === 'apply' && r.status === 'success')?.finished_at)}
        />
      </StatRow>

      <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
        {(data?.items ?? []).map((ds) => {
          const fresh = dryIsFresh(ds.last_dry, ttl);
          const isRunning = running?.dataset === ds.key;
          return (
            <Col key={ds.key} xs={24} md={12} xl={8}>
              <Card
                size="small"
                style={{
                  height: '100%',
                  borderColor: isRunning ? '#1677ff' : (ds.danger ? '#E8A33D' : undefined),
                }}
                title={
                  <Space>
                    <span>{ds.title}</span>
                    {ds.danger && <Tag color="warning">осторожно</Tag>}
                    {isRunning && <Tag color="processing" icon={<SyncOutlined spin />}>идёт</Tag>}
                  </Space>
                }
                extra={<Text type="secondary" style={{ fontSize: 12 }}>
                  {ds.rows != null ? `${ds.rows.toLocaleString('ru-RU')} строк` : ''}
                </Text>}
              >
                <Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 10 }}>
                  {ds.subtitle}
                </Paragraph>

                {ds.danger && (
                  <Alert type="warning" showIcon style={{ marginBottom: 10, fontSize: 12 }}
                         message={ds.danger} />
                )}

                {isRunning && running?.progress && (
                  <Alert
                    type="info" showIcon icon={<SyncOutlined spin />}
                    style={{ marginBottom: 10 }}
                    message={<Text style={{ fontSize: 12 }}>{running.progress}</Text>}
                  />
                )}

                <div style={{ fontSize: 12, color: L.muted, marginBottom: 10, lineHeight: 1.7 }}>
                  <div>
                    сухой прогон: {fmt(ds.last_dry?.finished_at || ds.last_dry?.started_at)}{' '}
                    <StatusTag status={ds.last_dry?.status} />
                  </div>
                  <div>
                    применение: {fmt(ds.last_apply?.finished_at || ds.last_apply?.started_at)}{' '}
                    <StatusTag status={ds.last_apply?.status} />
                  </div>
                </div>

                <Space>
                  <Button
                    icon={<EyeOutlined />}
                    disabled={busy}
                    onClick={() => start(ds, 'dry')}
                  >
                    Что изменится
                  </Button>
                  <Tooltip title={fresh ? '' : `Числа не старше ${ttl} ч — иначе сначала подсчёт`}>
                    <Button
                      type="primary"
                      danger={!!ds.danger}
                      icon={<ThunderboltOutlined />}
                      disabled={busy}
                      onClick={() => applyOrExplain(ds, fresh)}
                    >
                      Применить
                    </Button>
                  </Tooltip>
                  {ds.last_dry && (
                    <Button type="link" size="small" onClick={() => openDetail(ds.last_dry!.id)}>
                      отчёт
                    </Button>
                  )}
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>

      {!!batches.length && (
        <Card size="small" title="Общие обновления" style={{ marginTop: 16 }}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={batches}
            columns={[
              { title: '#', dataIndex: 'id', width: 64 },
              {
                title: 'Режим', dataIndex: 'mode', width: 130,
                render: (m: string) => m === 'dry'
                  ? <Tag>подсчёт</Tag> : <Tag>применение</Tag>,
              },
              { title: 'Статус', dataIndex: 'status', width: 120,
                render: (s) => <StatusTag status={s} /> },
              {
                title: 'Наборы', key: 'sets', width: 130,
                render: (_: any, b: SyncBatch) => `${b.done} из ${b.total}`,
              },
              {
                title: 'Итог', key: 'tot',
                render: (_: any, b: SyncBatch) => {
                  const t = b.stats?.totals;
                  if (!t) return <Text type="secondary">—</Text>;
                  return (
                    <Space size={4} wrap>
                      <Tag>+{t.added}</Tag>
                      <Tag>~{t.changed}</Tag>
                      <Tag>закрыто {t.closed}</Tag>
                      {!!t.refused && <Tag>отказов {t.refused}</Tag>}
                      {!!t.failed && <Tag>сбоев {t.failed}</Tag>}
                    </Space>
                  );
                },
              },
              { title: 'Кто', dataIndex: 'started_by_name', width: 150, render: (v) => v || '—' },
              { title: 'Когда', dataIndex: 'started_at', width: 150, render: fmt },
              {
                title: '', key: 'act', width: 110,
                render: (_: any, b: SyncBatch) => (
                  <Button size="small" icon={<EyeOutlined />} onClick={() => openBatch(b.id)}>
                    Отчёт
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Card size="small" title="История прогонов" style={{ marginTop: 16 }}>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={runColumns}
          dataSource={runs}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{ emptyText: <Empty description="Прогонов ещё не было" /> }}
        />
      </Card>

      <RunDetailModal run={detail} onClose={() => setDetail(null)} />
      <BatchDetailModal
        batch={batchDetail}
        onClose={() => setBatchDetail(null)}
        onOpenRun={openDetail}
      />

      <Modal
        open={!!confirm}
        title={`Применить набор «${confirm?.title}»?`}
        okText="Да, применить"
        okButtonProps={{ danger: true }}
        cancelText="Отмена"
        onOk={() => confirm && start(confirm, 'apply')}
        onCancel={() => setConfirm(null)}
      >
        <Alert type="warning" showIcon message={confirm?.danger} style={{ marginBottom: 12 }} />
        <Paragraph style={{ marginBottom: 0 }}>
          Перед записью снимается полный бэкап, путь к нему будет в отчёте прогона.
          Откат — одной командой.
        </Paragraph>
      </Modal>

      <Modal
        open={confirmBatch}
        title="Применить все наборы?"
        okText="Да, применить всё"
        cancelText="Отмена"
        onOk={() => startBatch('apply')}
        onCancel={() => setConfirmBatch(false)}
      >
        <Paragraph>
          Наборы пойдут очередью друг за другом. Перед записью каждого снимается бэкап,
          пути к ним будут в общем отчёте. Пропавшее из источника помечается закрытым,
          не удаляется.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          Займёт 15–25 минут. Страницу можно закрыть — работа идёт на сервере.
        </Paragraph>
      </Modal>
    </div>
  );
};

/** Идущая пачка: сколько наборов пройдено, какой сейчас и что он делает в эту секунду. */
const BatchProgress: React.FC<{
  batch: SyncBatch;
  running: DatasetsResponse['running'];
}> = ({ batch, running }) => {
  const percent = batch.total ? Math.round((batch.done / batch.total) * 100) : 0;
  return (
    <div>
      <Space style={{ marginBottom: 8 }} wrap>
        <Tag color="processing" icon={<SyncOutlined spin />}>
          {batch.mode === 'dry' ? 'считаю всё' : 'применяю всё'}
        </Tag>
        <Text strong>
          {batch.current_title || '—'} — {Math.min(batch.done + 1, batch.total)} из {batch.total}
        </Text>
      </Space>
      <Progress percent={percent} status="active" />
      <div style={{ fontSize: 12, color: L.muted, marginTop: 4 }}>
        {running?.progress
          ? <Text code style={{ fontSize: 12 }}>{running.progress}</Text>
          : 'начинаю набор…'}
      </div>
      <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        Страница обновляется сама. Её можно закрыть — работа идёт на сервере.
      </Paragraph>
    </div>
  );
};

/** Общий отчёт пачки: одна строка на набор + сводка. */
const BatchDetailModal: React.FC<{
  batch: SyncBatch | null;
  onClose: () => void;
  onOpenRun: (id: number) => void;
}> = ({ batch, onClose, onOpenRun }) => {
  const items: BatchItem[] = batch?.stats?.items ?? [];
  const totals = batch?.stats?.totals;
  const backups = items.filter((i) => i.backup);
  return (
    <Modal
      open={!!batch}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Закрыть</Button>}
      width={980}
      title={batch ? `Общее обновление #${batch.id} — ${
        batch.mode === 'dry' ? 'подсчёт' : 'применение'}` : ''}
    >
      {batch && (
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Space style={{ marginBottom: 12 }} wrap>
            <StatusTag status={batch.status} />
            <Text type="secondary">{fmt(batch.started_at)} → {fmt(batch.finished_at)}</Text>
            {batch.started_by_name && <Text type="secondary">· {batch.started_by_name}</Text>}
            <Text type="secondary">· наборов {batch.done} из {batch.total}</Text>
          </Space>

          {batch.error && (
            <Alert type="error" showIcon style={{ marginBottom: 12 }}
                   message="Сбой" description={batch.error} />
          )}

          {totals && (
            <StatRow span={6}>
              <StatCard label="Из источника" value={totals.fetched} />
              <StatCard label="Добавлено" value={totals.added} color="#2E9E5B" colorValue />
              <StatCard label="Изменилось" value={totals.changed} color="#C08A2A" colorValue />
              <StatCard label="Закрыто" value={totals.closed} color="#C0392B" colorValue />
            </StatRow>
          )}

          <Table
            rowKey="key"
            size="small"
            style={{ marginTop: 12 }}
            pagination={false}
            dataSource={items}
            columns={[
              { title: 'Набор', dataIndex: 'title', width: 220 },
              { title: 'Статус', dataIndex: 'status', width: 120,
                render: (s) => <StatusTag status={s} /> },
              { title: 'Из источника', dataIndex: 'fetched', width: 120 },
              { title: 'Добавлено', dataIndex: 'added', width: 110 },
              { title: 'Изменилось', dataIndex: 'changed', width: 110 },
              { title: 'Закрыто', dataIndex: 'closed', width: 100 },
              {
                title: 'Почему не применилось', dataIndex: 'error',
                render: (e: string | null) => e
                  ? <Text type="secondary" style={{ fontSize: 12 }}>{e}</Text>
                  : <Text type="secondary">—</Text>,
              },
              {
                title: '', key: 'act', width: 100,
                render: (_: any, i: BatchItem) => (
                  <Button size="small" onClick={() => onOpenRun(i.run_id)}>Отчёт</Button>
                ),
              },
            ]}
          />

          {!!backups.length && (
            <Card size="small" title="Бэкапы этого обновления" style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, lineHeight: 1.9 }}>
                {backups.map((i) => (
                  <div key={i.key}>
                    <Text>{i.title}: </Text><Text code>{i.backup}</Text>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </Modal>
  );
};

/** Отчёт прогона: числа, поля, примеры записей и путь к бэкапу. */
const RunDetailModal: React.FC<{ run: SyncRun | null; onClose: () => void }> = ({ run, onClose }) => {
  const s = run?.stats;
  return (
    <Modal
      open={!!run}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Закрыть</Button>}
      width={900}
      title={run ? `Прогон #${run.id} — ${run.dataset_title} (${
        run.mode === 'dry' ? 'сухой прогон' : 'применение'})` : ''}
    >
      {run && (
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Space style={{ marginBottom: 12 }} wrap>
            <StatusTag status={run.status} />
            <Text type="secondary">{fmt(run.started_at)} → {fmt(run.finished_at)}</Text>
            {run.started_by_name && <Text type="secondary">· {run.started_by_name}</Text>}
          </Space>

          {run.error && (
            <Alert type={run.status === 'refused' ? 'warning' : 'error'} showIcon
                   style={{ marginBottom: 12 }}
                   message={run.status === 'refused' ? 'Прогон отказался применять' : 'Сбой'}
                   description={run.error} />
          )}

          {s && (
            <>
              <StatRow span={6}>
                <StatCard label="Из источника" value={s.fetched} />
                <StatCard label="Добавлено" value={run.mode === 'apply' ? (s.written?.added ?? 0) : s.added}
                          color="#2E9E5B" colorValue />
                <StatCard label="Изменилось" value={run.mode === 'apply' ? (s.written?.changed ?? 0) : s.changed}
                          color="#C08A2A" colorValue />
                <StatCard label="Пропало (закрыто)" value={run.mode === 'apply' ? (s.written?.closed ?? 0) : s.missing}
                          color="#C0392B" colorValue />
              </StatRow>

              {!!s.notes?.length && (
                <Alert type="info" style={{ margin: '12px 0' }} showIcon
                       message="На что обратить внимание"
                       description={<ul style={{ margin: 0, paddingLeft: 18 }}>
                         {s.notes.map((n, i) => <li key={i}>{n}</li>)}
                       </ul>} />
              )}

              {!!Object.keys(s.changed_fields || {}).length && (
                <Card size="small" title="Какие поля менялись" style={{ marginTop: 12 }}>
                  <Space wrap>
                    {Object.entries(s.changed_fields).map(([f, n]) => (
                      <Tag key={f}>{f}: {n}</Tag>
                    ))}
                  </Space>
                </Card>
              )}

              <SampleList title="Появились" rows={s.added_samples} />
              <SampleList title="Изменились" rows={s.changed_samples} withChanges />
              <SampleList title="Пропали из источника (помечены закрытыми, не удалены)"
                          rows={s.missing_samples} />

              {(s.backup_table || s.backup_file) && (
                <Card size="small" title="Бэкап" style={{ marginTop: 12 }}>
                  {s.backup_table && <div><Text code>{s.backup_table}</Text> — снимок в базе</div>}
                  {s.backup_file && <div><Text code>{s.backup_file}</Text> — файл</div>}
                  {s.restore_command && (
                    <Paragraph copyable={{ text: s.restore_command }}
                               style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                      <Text code style={{ whiteSpace: 'pre-wrap' }}>{s.restore_command}</Text>
                    </Paragraph>
                  )}
                </Card>
              )}

              {!!s.written && Object.keys(s.written).length > 0 && (
                <Card size="small" title="Итог записи" style={{ marginTop: 12 }}>
                  <pre style={{ fontSize: 11.5, margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(s.written, null, 2)}
                  </pre>
                </Card>
              )}
            </>
          )}

          {run.log && (
            <Card size="small" title="Лог" style={{ marginTop: 12 }}>
              <pre style={{ fontSize: 11.5, margin: 0, whiteSpace: 'pre-wrap' }}>{run.log}</pre>
            </Card>
          )}
        </div>
      )}
    </Modal>
  );
};

/** Числа без примеров бесполезны — поэтому в отчёте всегда есть имена записей. */
const SampleList: React.FC<{
  title: string;
  rows?: { key: string; label: string; changes?: Record<string, [any, any]> }[];
  withChanges?: boolean;
}> = ({ title, rows, withChanges }) => {
  if (!rows?.length) return null;
  return (
    <Card size="small" title={`${title} — первые ${rows.length}`} style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12.5, lineHeight: 1.8 }}>
        {rows.map((r, i) => (
          <div key={`${r.key}-${i}`}>
            <Text strong>{r.label || r.key || '—'}</Text>
            {withChanges && r.changes && (
              <span style={{ color: L.muted }}>
                {Object.entries(r.changes).map(([f, v]) => (
                  <span key={f}> · {f}: <Text delete type="secondary">{String(v?.[0] ?? '—').slice(0, 60)}</Text>
                    {' → '}<Text>{String(v?.[1] ?? '—').slice(0, 60)}</Text></span>
                ))}
              </span>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default DataSyncPage;
